import { defineStore } from 'pinia'
import { reactive, computed } from 'vue'
import ERROR_TYPES from '@/constants/errorTypes'
import wait from '@/utilities/wait'
import { datadogService } from '@/services/datadog'
import { levelForErrorCause } from '@/services/datadogLogPolicy'

export const useErrorStore = defineStore('error', () => {
  const context: ErrorContext = reactive({
    cause: '',
    items: [],
    isShow: false,
    animationDelay: 0,
  })

  const hasFieldErrors = computed<boolean>(() => {
    return context.cause === ERROR_TYPES.VALIDATION_ERROR && context.items.length > 0
  })

  const hasGlobalErrors = computed<boolean>(() => {
    return context.cause === ERROR_TYPES.GLOBAL_ERROR && context.items.length > 0
  })

  const hasErrors = computed<boolean>(() => {
    return hasFieldErrors.value || hasGlobalErrors.value
  })

  const setError = (cause: ErrorType, item: ErrorItem) => {
    context.cause = cause
    context.items.push(item)
    context.isShow = true

    // Track error in Datadog
    const errorContext = {
      error_type: 'application_error',
      error_cause: cause,
      field: item.field,
      messages: item.messages,
    }

    // Create error for tracking
    const error = new Error(`${cause}: ${item.messages.join(', ')}`)
    datadogService.trackError(error, errorContext, levelForErrorCause(cause))
  }

  /**
   * エラークリア処理
   * @param delay エラークリア発火から実際にエラーメッセージ削除するまでの待機時間（＝画面側のエラー表示が完全に消えるまでの CSS アニメーション時間）
   *
   * エラークリア発火時にすぐにメッセージを消去すると、
   * 画面のエラーメッセージエリアがフェードアウトアニメーション中にメッセージが空になってしまい、
   * 表示に違和感が出てしまうので、CSS アニメーションの終了を待ってからエラーメッセージを空にする必要がある。
   * よって、引数の delay 時間は CSS の transition-time と同期される。
   */
  const clearErrors = async (callback?: Callback) => {
    context.isShow = false

    const delay: number = 0.3
    context.animationDelay = delay

    // 既にエラーメッセージが表示されている場合のみ、エラーのワイプアウト CSS アニメーション分の時間を待機させる
    if (hasErrors.value) {
      await wait(context.animationDelay)
    }

    context.items = []
    if (callback) callback()
  }

  const handleErrors = (error: ErrorContext) => {
    // Datadog への送信は axiosClient の response interceptor が担う（同じ失敗を error_type 違いで重ねない）
    context.cause = error.cause
    context.items = error.items
  }

  /**
   * 指定のフィールド名のメッセージを抽出
   * @param field 取得したいエラーメッセージフィールド名
   * @returns エラーメッセージの string 配列
   */
  const getErrorMessagesByFieldName = (field: string): string[] => {
    if (!field) return []
    const item = context.items.find(item => item.field && item.field === field)
    return item ? item.messages : []
  }

  return {
    // data
    context,

    // computed
    hasFieldErrors,

    // methods
    setError,
    clearErrors,
    handleErrors,
    getErrorMessagesByFieldName,
  }
})
